import React, { useState } from "react";
import Loading from "../../components/Loading";
import { submit } from "../../actions/studensts";
import { useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import TermsAndConditions from "./terms-and-conditions";

const studentInitialData = {
  full_name: "",
  first_name: "",
  father_name: "",
  passport_number: "",
  phone: "",
  parent_phone: "",
  email: "",
  exam_type: "",
  act1_id: "",
  act2_id: "",
  submit_date: formatDate(new Date(Date.now())),
  category: "outside",
};

export default function Form() {
  const [studentData, setStudentData] = useState(studentInitialData);
  const [loading, setLoading] = useState(false);
  const [universityCount, setUniversityCount] = useState(1);
  const [universities, setUniversities] = useState([""]);
  const [specializationCount, setSpecializationCount] = useState(1);
  const [specializations, setSpecializations] = useState([""]);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const date = formatDate(new Date(Date.now()));

    const payload = { ...studentData };

    // Map first_name from "Student Name" and full_name from "Printed Full Name"
    // first_name is already in payload from the form field

    // Map father_name to middle1_name (existing DB column)
    payload.middle1_name = payload.father_name;
    delete payload.father_name;

    // Combine phone and parent_phone into phone field
    if (payload.parent_phone) {
      payload.phone = payload.phone + " / " + payload.parent_phone;
    }
    delete payload.parent_phone;

    // Map universities to collegeName fields for student_colleges table
    universities.forEach((uni, i) => {
      if (uni) {
        payload[`collegeName${i + 1}`] = uni;
      }
    });

    // Map specializations to specialization fields for student_specializations table
    specializations.forEach((spec, i) => {
      if (spec) {
        payload[`specialization${i + 1}`] = spec;
      }
    });

    // Clear conditional fields based on exam_type
    if (payload.exam_type === "ACT") {
      delete payload.act2_id;
    } else if (payload.exam_type === "AIST") {
      delete payload.act1_id;
    }

    const res = await submit(payload, date);
    setLoading(false);

    if (res?.status === 201) {
      navigate("/redirect", {
        state: {
          request: "good",
          message: res.data,
        },
      });
      return;
    }

    navigate("/redirect", {
      state: {
        request: res?.data?.request || "bad",
        message: res?.data,
      },
    });
  };

  const handleInputChange = (e) => {
    const target = e.target;
    const name = target.name;
    let value;

    if (target.type === "checkbox") {
      value = target.checked;
    } else if (target.type === "number") {
      const int_value = parseInt(target.value, 10);
      value = isNaN(int_value) ? 0 : int_value;
    } else {
      value = target.value;
    }

    setStudentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleExamTypeChange = (type) => {
    setStudentData((prev) => {
      let newType;
      if (prev.exam_type === "Both") {
        newType = type === "ACT" ? "AIST" : "ACT";
      } else if (prev.exam_type === type) {
        newType = "";
      } else if (prev.exam_type === "") {
        newType = type;
      } else {
        newType = "Both";
      }
      return {
        ...prev,
        exam_type: newType,
      };
    });
  };

  const handleUniversityChange = (index, value) => {
    setUniversities((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addUniversity = () => {
    if (universityCount < 5) {
      setUniversityCount((c) => c + 1);
      setUniversities((prev) => [...prev, ""]);
    }
  };

  const handleSpecializationChange = (index, value) => {
    setSpecializations((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addSpecialization = () => {
    if (specializationCount < 3) {
      setSpecializationCount((c) => c + 1);
      setSpecializations((prev) => [...prev, ""]);
    }
  };

  const showACT = studentData.exam_type === "ACT" || studentData.exam_type === "Both";
  const showAIST = studentData.exam_type === "AIST" || studentData.exam_type === "Both";

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        <form
          onSubmit={handleSubmit}
          action="submit"
          className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-8 space-y-2"
        >
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white">Add Your Information</h1>
            <p className="text-slate-400 text-sm mt-1">
              For best experience use Google Chrome or Firefox.
              Safari users: version 12.0 or higher required.
            </p>
          </div>

          {/* 1 - Student Name (Required) → maps to first_name */}
          <Input
            type="text"
            placeholder="Student Name"
            name="first_name"
            onChange={handleInputChange}
            value={studentData.first_name}
            pattern="^[a-zA-Z0-9\\-\\u0590-\\u05FF ]+$"
            title="Please enter english language only"
          />

          {/* 2 - Father Name (Required) → maps to middle1_name */}
          <Input
            type="text"
            placeholder="Father Name"
            name="father_name"
            onChange={handleInputChange}
            value={studentData.father_name}
          />

          {/* 3 - National ID or Passport Number (Required) */}
          <Input
            type="text"
            placeholder="National ID or Passport Number"
            name="passport_number"
            onChange={handleInputChange}
            value={studentData.passport_number}
          />

          {/* 4 - Phone Number (Required) */}
          <Input
            type="text"
            placeholder="Phone Number"
            name="phone"
            onChange={handleInputChange}
            value={studentData.phone}
          />

          {/* 6 - Parent Number (Required) → stored with phone */}
          <Input
            type="text"
            placeholder="Parent Number"
            name="parent_phone"
            onChange={handleInputChange}
            value={studentData.parent_phone}
          />

          {/* 7 - Email (Required) */}
          <Input
            type="email"
            placeholder="Email"
            name="email"
            onChange={handleInputChange}
            value={studentData.email}
          />

          {/* 8 - Choose ACT or AIST or Both (Required) */}
          <div className="m-2 flex flex-col items-center justify-center">
            <label className="text-slate-300 mb-2">
              <span className="text-red-400">*</span> Exam Type:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={studentData.exam_type === "ACT" || studentData.exam_type === "Both"}
                  onChange={() => handleExamTypeChange("ACT")}
                  className="accent-indigo-500 w-4 h-4"
                />
                ACT
              </label>
              <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={studentData.exam_type === "AIST" || studentData.exam_type === "Both"}
                  onChange={() => handleExamTypeChange("AIST")}
                  className="accent-indigo-500 w-4 h-4"
                />
                ACT Inernational Subject Test
              </label>
            </div>
            <input
              type="text"
              value={studentData.exam_type}
              required
              onChange={() => {}}
              className="opacity-0 h-0 w-0 absolute"
              tabIndex={-1}
            />
          </div>

          {/* ACT Trial section (ID only) */}
          {showACT && (
            <div className="rounded-xl bg-indigo-600/5 border border-indigo-500/20 p-4 space-y-2">
              <h3 className="text-indigo-400 text-sm font-semibold">ACT Trial</h3>
              <Input
                type="text"
                placeholder="ACT ID"
                name="act1_id"
                onChange={handleInputChange}
                value={studentData.act1_id}
              />
            </div>
          )}

          {/* AIST Trial section (ID only) */}
          {showAIST && (
            <div className="rounded-xl bg-emerald-600/5 border border-emerald-500/20 p-4 space-y-2">
              <h3 className="text-emerald-400 text-sm font-semibold">ACT Inernational Subject Test Trial</h3>
              <Input
                type="text"
                placeholder="AIST ID"
                name="act2_id"
                onChange={handleInputChange}
                value={studentData.act2_id}
              />
            </div>
          )}

          {/* 13 - Universities (dynamic, 1 required, up to 5) */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-slate-300 text-sm font-semibold">Universities</h3>
              {universityCount < 5 && (
                <button
                  type="button"
                  onClick={addUniversity}
                  className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-sm font-semibold transition-colors"
                >
                  <span className="text-lg leading-none">+</span> Add University
                </button>
              )}
            </div>
            {universities.map((uni, index) => (
              <div key={index} className="m-2 flex items-center justify-center">
                <label className="flex-auto text-slate-300">
                  University {index + 1}:
                </label>
                <input
                  autoComplete="off"
                  type="text"
                  placeholder={`University ${index + 1}`}
                  className="flex-initial p-2 ml-1 border border-white/10 rounded-xl bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  value={uni}
                  onChange={(e) => handleUniversityChange(index, e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            ))}
          </div>

          {/* Specializations (dynamic, 1 required, up to 3) */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-slate-300 text-sm font-semibold">Specializations</h3>
              {specializationCount < 3 && (
                <button
                  type="button"
                  onClick={addSpecialization}
                  className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-sm font-semibold transition-colors"
                >
                  <span className="text-lg leading-none">+</span> Add Specialization
                </button>
              )}
            </div>
            {specializations.map((spec, index) => (
              <div key={index} className="m-2 flex items-center justify-center">
                <label className="flex-auto text-slate-300">
                  Specialization {index + 1}:
                </label>
                <input
                  autoComplete="off"
                  type="text"
                  placeholder={`Specialization ${index + 1}`}
                  className="flex-initial p-2 ml-1 border border-white/10 rounded-xl bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  value={spec}
                  onChange={(e) => handleSpecializationChange(index, e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            ))}
          </div>

          <div className="m-2 mt-3 flex items-center justify-center text-right">
            <input
              autoComplete="off"
              type="checkbox"
              className="flex-initial p-2 ml-1 border border-white/10 rounded-xl accent-indigo-500"
              required={true}
            />
            <label htmlFor="send score" className="flex-auto dir-pthree text-slate-300">
              <span className="text-red-400 ">*</span>
              <span className="text-base font-medium">نرجوا التاكد من عمل </span>
              <span>
                <a
                  className="text-indigo-400 underline"
                  href="https://www.act.org/content/act/en/products-and-services/the-act/scores/sending-your-scores.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  Send score
                </a>
              </span>
              <span className="text-base font-medium">
                &nbsp;للمحاولات المطلوبة&nbsp;
              </span>
            </label>
          </div>

          <div>
            <TermsAndConditions />
            <div>
              <Input
                type="text"
                placeholder="Printed Full Name"
                name="full_name"
                onChange={handleInputChange}
                value={studentData.full_name}
              />
              <Input
                type="date"
                placeholder="Today's Date"
                name="submit_date"
                value={studentData.submit_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="flex flex-row-reverse items-center justify-between mt-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-white/10 disabled:text-slate-500 text-white font-semibold px-8 py-3 rounded-xl transition-all text-sm"
            >
              {!loading ? "Submit" : <Loading />}
            </button>
          </div>
          <span className="text-red-400 text-sm">*required field</span>
        </form>
      </div>
    </div>
  );
}

function formatDate(today) {
  return today?.toISOString().split("T")[0];
}